/**
 * Error kinds the UI distinguishes. Every data-source failure is normalised to
 * an `ApiError` by `store/api/fromSource.ts` before it reaches a component.
 */
export type ApiErrorKind =
  | 'notConnected' // the live endpoint is not implemented yet
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'validation'
  | 'network'
  | 'aborted'
  | 'server'
  | 'unknown';

export type ApiError = {
  kind: ApiErrorKind;
  message: string;
  status?: number;
  /** Backend `customCode`, when the envelope carried one. */
  code?: string;
};

/** Thrown by live loaders that have no API behind them yet. Renders as "not connected". */
export class NotImplementedError extends Error {
  readonly endpoint: string;
  constructor(endpoint: string) {
    super(`Live data for "${endpoint}" is not connected yet.`);
    this.name = 'NotImplementedError';
    this.endpoint = endpoint;
  }
}

export class HttpError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.code = code;
  }
}

/**
 * A response that does not match the DTO a live loader expects. Fails loudly
 * rather than drawing a wrong chart; the zod issues are kept for the console.
 */
export class ContractError extends Error {
  readonly endpoint: string;
  readonly issues: unknown;
  constructor(endpoint: string, issues: unknown) {
    super(`The server sent "${endpoint}" in an unexpected shape.`);
    this.name = 'ContractError';
    this.endpoint = endpoint;
    this.issues = issues;
  }
}

export class SourceUnavailableError extends Error {
  constructor(source: string) {
    super(`The "${source}" data source is not available in this build.`);
    this.name = 'SourceUnavailableError';
  }
}

function kindForStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 400 || status === 422) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

/** An `ApiError` that travelled as an exception (e.g. through a data-source requester). */
export class ApiErrorException extends Error {
  readonly apiError: ApiError;
  constructor(apiError: ApiError) {
    super(apiError.message);
    this.name = 'ApiErrorException';
    this.apiError = apiError;
  }
}

/**
 * The human message in an error body. Handles the envelope's `string[]`, a
 * plain string, and deepdive-v2's exception filter, which nests Nest's own
 * response: `{ statusCode, message: { message: string | string[], error } }`.
 */
export function messageFrom(body: unknown, depth = 0): string | undefined {
  if (typeof body === 'string') return body.trim() || undefined;
  if (Array.isArray(body)) {
    const parts = body.map((part) => messageFrom(part, depth + 1)).filter(Boolean);
    return parts.length ? parts.join(' ') : undefined;
  }
  if (body && typeof body === 'object' && depth < 3) {
    const record = body as Record<string, unknown>;
    return messageFrom(record.message, depth + 1) ?? messageFrom(record.error, depth + 1);
  }
  return undefined;
}

/** RTK `fetchBaseQuery` error → ApiError. */
export function fromFetchError(error: {
  status: number | string;
  data?: unknown;
  error?: string;
}): ApiError {
  const data = error.data as { customCode?: unknown } | undefined;
  const code = typeof data?.customCode === 'string' ? data.customCode : undefined;
  if (typeof error.status === 'number') {
    const message = messageFrom(error.data) ?? `Request failed (${error.status})`;
    return { kind: kindForStatus(error.status), message, status: error.status, code };
  }
  if (error.status === 'PARSING_ERROR')
    return { kind: 'server', message: 'The server sent an unreadable response.' };
  // A 200 envelope with `success: false` (see store/api/baseQuery.ts).
  if (error.status === 'CUSTOM_ERROR') {
    return {
      kind: 'server',
      message: messageFrom(error.data) ?? error.error ?? 'The server reported a failure.',
      code,
    };
  }
  if (error.status === 'TIMEOUT_ERROR')
    return { kind: 'network', message: 'The request timed out.' };
  return { kind: 'network', message: error.error ?? 'Network error' };
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiErrorException) return error.apiError;
  if (error instanceof NotImplementedError) return { kind: 'notConnected', message: error.message };
  if (error instanceof ContractError) return { kind: 'server', message: error.message };
  if (error instanceof HttpError) {
    return {
      kind: kindForStatus(error.status),
      message: error.message,
      status: error.status,
      code: error.code,
    };
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return { kind: 'aborted', message: 'The request was cancelled.' };
  }
  if (error instanceof TypeError) return { kind: 'network', message: error.message };
  if (error instanceof Error) return { kind: 'unknown', message: error.message };
  return { kind: 'unknown', message: String(error) };
}

export function isApiError(value: unknown): value is ApiError {
  return typeof value === 'object' && value !== null && 'kind' in value && 'message' in value;
}
